import {
  Zap, Eye, Users, Clock, TrendingUp, BarChart3,
  Globe, Smartphone, Monitor, MapPin,
} from "lucide-react";
import { StatCard, BarChart, ProgressBar } from "./AdminComponents";

const AdminAnalytics = ({ ana, period, setPeriod }) => (
  <div className="space-y-6" data-testid="admin-analytics-tab">
    {/* Realtime */}
    <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
      <StatCard icon={Zap} label="Active Now" value={ana.realtime?.active_now || 0} sub="Last 30 min" color="emerald" />
      <StatCard icon={Eye} label="Total Page Views" value={(ana.traffic?.total_views || 0).toLocaleString()} color="sky" />
      <StatCard icon={Users} label="Unique Visitors" value={(ana.traffic?.unique_visitors || 0).toLocaleString()} color="violet" />
      <StatCard icon={Clock} label="Avg Session" value={ana.sessions?.avg_duration_display || "0m 0s"} sub={`${ana.sessions?.total || 0} total sessions`} color="amber" />
      <StatCard icon={TrendingUp} label="Bounce Rate" value={`${ana.sessions?.bounce_rate || 0}%`} sub={`${ana.sessions?.bounced || 0} single-page sessions`} color="rose" />
      <StatCard icon={BarChart3} label="Avg Daily Views" value={ana.traffic?.avg_daily_views || 0} sub={`Top: ${ana.traffic?.top_page || "-"}`} color="sky" />
    </div>

    {/* Period selector */}
    <div className="flex gap-2">
      {[7, 30, 90].map((p) => (
        <button key={p} onClick={() => setPeriod(p)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium ${period === p ? "bg-violet-500/20 text-violet-300 border border-violet-500/30" : "bg-white/5 text-slate-400 border border-white/5"}`}
          data-testid={`period-${p}`}
        >{p}d</button>
      ))}
    </div>

    {/* Daily Traffic Chart */}
    <div className="bg-slate-900/70 rounded-xl border border-white/5 p-5">
      <h3 className="text-sm font-semibold text-white mb-1">Daily Traffic</h3>
      <p className="text-[11px] text-slate-500 mb-4">Views & Visitors</p>
      <BarChart data={ana.daily_traffic || []} dataKey="views" height={120} color="#0ea5e9" />
    </div>

    {/* Page Breakdown + Devices + Sources */}
    <div className="grid md:grid-cols-2 gap-4">
      <div className="bg-slate-900/70 rounded-xl border border-white/5 p-5">
        <h3 className="text-sm font-semibold text-white mb-4">Page Breakdown</h3>
        <div className="space-y-3">
          {(ana.pages || []).map((p) => (
            <div key={p.page} className="space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-slate-300 font-mono">{p.page}</span>
                <span className="text-slate-400">{p.pct}%</span>
              </div>
              <ProgressBar pct={p.pct} color="sky" />
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-slate-900/70 rounded-xl border border-white/5 p-5">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-slate-400" /> Devices
          </h3>
          <div className="space-y-2">
            {(ana.devices || []).map((d) => (
              <div key={d.name} className="flex items-center gap-3">
                <span className="text-xs text-slate-300 w-16">{d.name}</span>
                <ProgressBar pct={d.pct} color="violet" />
                <span className="text-xs text-slate-400 w-10 text-right">{d.pct}%</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-slate-900/70 rounded-xl border border-white/5 p-5">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Monitor className="w-4 h-4 text-slate-400" /> Browsers
          </h3>
          <div className="space-y-2">
            {(ana.browsers || []).map((b) => (
              <div key={b.name} className="flex items-center gap-3">
                <span className="text-xs text-slate-300 w-16">{b.name}</span>
                <ProgressBar pct={b.pct} color="emerald" />
                <span className="text-xs text-slate-400 w-10 text-right">{b.pct}%</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-slate-900/70 rounded-xl border border-white/5 p-5">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Globe className="w-4 h-4 text-slate-400" /> Traffic Sources
          </h3>
          <div className="space-y-2">
            {(ana.sources || []).map((s) => (
              <div key={s.name} className="flex items-center gap-3">
                <span className="text-xs text-slate-300 w-20">{s.name}</span>
                <ProgressBar pct={s.pct} color="amber" />
                <span className="text-xs text-slate-400 w-10 text-right">{s.pct}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* Geographic + Cities */}
    <div className="grid md:grid-cols-2 gap-4">
      <div className="bg-slate-900/70 rounded-xl border border-white/5 p-5">
        <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-slate-400" /> Countries (by sessions)
        </h3>
        {(ana.geo || []).length === 0 ? (
          <p className="text-xs text-slate-500">No session data yet. Sessions are tracked as users browse the app.</p>
        ) : (
          <div className="space-y-3">
            {(ana.geo || []).map((g) => (
              <div key={g.country} className="space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-300">{g.country}</span>
                  <span className="text-slate-400">{g.users} ({g.pct}%)</span>
                </div>
                <ProgressBar pct={g.pct} color="violet" />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-slate-900/70 rounded-xl border border-white/5 p-5">
        <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
          <Globe className="w-4 h-4 text-slate-400" /> Cities (by sessions)
        </h3>
        {(ana.cities || []).length === 0 ? (
          <p className="text-xs text-slate-500">No city data yet.</p>
        ) : (
          <div className="space-y-2">
            {(ana.cities || []).map((c) => (
              <div key={c.city} className="flex items-center justify-between">
                <span className="text-sm text-slate-300">{c.city}</span>
                <span className="text-xs text-slate-400 font-medium">{c.sessions}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);

export default AdminAnalytics;
